import { Component, computed, inject, input, output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { TranslatePipe } from '@ngx-translate/core';

import { ICONS } from '@core/services/icons/icons.service';

import { ModalComponent, ModalSize } from '@shared/components/modal/modal.component';

import { ResolvedDeployView } from '../deploy-view.utils';

/** Modal rendering one built message or body: HTML in an iframe, anything else as text. */
@Component({
  selector: 'app-deploy-content-viewer',
  imports: [TranslatePipe, ModalComponent],
  template: `
    <app-modal
      [title]="title()"
      [size]="ModalSize.XL"
      [config]="{ dismissible: true, closeOnBackdropClick: true }"
      (closeModal)="closed.emit()">
      <div modal-body class="flex flex-col gap-2">
        <div class="flex items-start justify-between gap-3">
          @if (view().subject; as subject) {
            <div class="flex min-w-0 flex-col gap-0.5">
              <span
                class="text-text-tertiary text-[10px] font-medium tracking-wide uppercase">
                {{ 'features.plan.deploy.content_subject' | translate }}
              </span>
              <p class="text-text-primary truncate text-sm">{{ subject }}</p>
            </div>
          }

          <button
            type="button"
            class="text-text-secondary hover:text-text-primary hover:bg-interactive ml-auto inline-flex shrink-0 cursor-pointer items-center gap-1.5 rounded-sm px-1.5 py-0.5 text-xs transition-colors"
            (click)="copyRequested.emit(view().body)">
            <i [class]="ICONS.copy" class="text-xs"></i>
            {{ 'features.plan.deploy.copy_content' | translate }}
          </button>
        </div>

        @if (view().isHtml) {
          <!-- sandbox="" (no allow-same-origin / allow-scripts) so trusted srcdoc HTML
               cannot reach the parent origin, DOM, or tokens. -->
          <iframe
            [srcdoc]="safeHtml()"
            sandbox=""
            class="border-default h-[60vh] w-full rounded-sm border"></iframe>
        } @else {
          <pre
            class="border-default text-text-primary max-h-[60vh] overflow-auto rounded-sm border p-3 font-mono text-[11px] whitespace-pre-wrap"
            >{{ view().body }}</pre
          >
        }
      </div>
    </app-modal>
  `,
  styles: ``
})
export class DeployContentViewerComponent {
  readonly title = input.required<string>();
  readonly view = input.required<ResolvedDeployView>();

  readonly closed = output<void>();
  readonly copyRequested = output<string>();

  protected readonly ICONS = ICONS;
  protected readonly ModalSize = ModalSize;

  private readonly sanitizer = inject(DomSanitizer);

  protected readonly safeHtml = computed<SafeHtml | null>(() => {
    const view = this.view();
    return view.isHtml ? this.sanitizer.bypassSecurityTrustHtml(view.body) : null;
  });
}
